import { apiClient } from './client'
import type { CursorPage } from './accounts'

/**
 * Read shape for `Program` (`docs/data-dictionary.md` Cluster D) — mirrors the backend contract
 * (`backend/.../module/cartridge/api/dto/ProgramView.java`) exactly.
 */
export interface ProgramView {
  id: string
  jurisdiction: string
  level: string
  parentProgramId: string | null
  code: string
  kind: string
  createdAt: string
  createdBy: string | null
  updatedAt: string
  updatedBy: string | null
  version: number
}

/**
 * Thin additive wrapper (this UX-quality pass) over the already-published `GET
 * /api/v1/programs/{id}` (`ProgramController.get`, `module.cartridge`) — no new backend route was
 * needed. Every screen that only ever had a bare `programId` to show (Case-workspace Overview/
 * Credit/Monetization/Authority/Eligibility tabs, which read the narrower `ProgramFilingDetailView`
 * that carries no denormalized `programName`/`jurisdiction`, unlike the tenant-wide
 * `ProgramFilingListView`) resolves the real `code`/`jurisdiction` through this client instead of
 * rendering "Program {uuid}".
 */
export async function getProgram(id: string, signal?: AbortSignal): Promise<ProgramView> {
  const { data } = await apiClient.get<ProgramView>(`/programs/${id}`, { signal })
  return data
}

export interface ListProgramsParams {
  /** RSQL, already combined by the caller (see lib/rsql.ts). */
  filter?: string | null
  sort?: string | null
  cursor?: string | null
  limit?: number
}

function toQuery(params: ListProgramsParams): string {
  const q = new URLSearchParams()
  if (params.filter) q.set('filter', params.filter)
  if (params.sort) q.set('sort', params.sort)
  if (params.cursor) q.set('cursor', params.cursor)
  q.set('limit', String(params.limit ?? 50))
  return q.toString()
}

/**
 * IB-326/IB-327 — `GET /api/v1/programs` (`ProgramController.list`). Backs the "New Case" wizard's
 * federal/state applicability picker and the "New Production" wizard's base-state-program select.
 */
export async function listPrograms(
  params: ListProgramsParams,
  signal?: AbortSignal,
): Promise<CursorPage<ProgramView>> {
  const { data } = await apiClient.get<CursorPage<ProgramView>>(`/programs?${toQuery(params)}`, { signal })
  return data
}
